import React from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Stack, useTheme } from "@mui/material";
import MessageIcon from "@mui/icons-material/Message";
import moment from "moment";

import Button from "components/basic/button";
import Typography from "components/basic/typography";
import { formatMoney } from "utils/formatMoney";

import { EVENT_TYPE_MAP } from "../../../../../calendar/eventTypes";
import { LeadCustomer } from "../../../../../calendar/booking/types";
import { Meeting } from "../../../../types";

const RailCard: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Typography
        variant="caption"
        style={{
          color: theme.palette.text.secondary,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {title}
      </Typography>
      <Box sx={{ mt: 0.75 }}>{children}</Box>
    </Box>
  );
};

interface MeetingSummaryRailProps {
  meeting: Meeting;
  leadCustomer?: LeadCustomer;
  onViewQuotes: () => void;
  onMessage?: () => void;
}

const MeetingSummaryRail: React.FC<MeetingSummaryRailProps> = ({
  meeting,
  leadCustomer,
  onViewQuotes,
  onMessage,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("meetings");
  const meta = EVENT_TYPE_MAP[meeting.type];
  const Icon = meta.icon;
  const isVideo = meeting.type === "video";

  return (
    <Stack spacing={1.5} sx={{ width: { xs: "100%", md: 280 }, flexShrink: 0 }}>
      <RailCard title={t("rail.type")}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Icon sx={{ color: meta.color }} />
          <Box>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {meta.label}
            </Typography>
            {isVideo && (
              <Typography
                variant="caption"
                style={{ color: theme.palette.text.secondary }}
              >
                {t("rail.video_hint")}
              </Typography>
            )}
          </Box>
        </Stack>
      </RailCard>

      <RailCard title={t("rail.customer")}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {meeting.customerName}
          </Typography>
          {onMessage && (
            <IconButton
              size="small"
              title={t("rail.message")}
              onClick={onMessage}
              sx={{ color: theme.palette.warning.main }}
            >
              <MessageIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
        {meeting.customerPhone && (
          <Typography
            variant="caption"
            style={{ color: theme.palette.text.secondary, display: "block" }}
          >
            {meeting.customerPhone}
          </Typography>
        )}
        {meeting.customerEmail && (
          <Typography
            variant="caption"
            style={{ color: theme.palette.text.secondary }}
          >
            {meeting.customerEmail}
          </Typography>
        )}
      </RailCard>

      {meeting.estimate && (
        <RailCard title={t("rail.job")}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                {meeting.estimate.estimateNumber
                  ? t("rail.job_number", {
                      number: meeting.estimate.estimateNumber,
                    })
                  : t("rail.estimate")}
              </Typography>
              {meeting.estimate.chargerModel && (
                <Typography
                  variant="caption"
                  style={{ color: theme.palette.text.secondary }}
                >
                  {meeting.estimate.chargerModel}
                </Typography>
              )}
            </Box>
            <Typography variant="body2" style={{ fontWeight: 700 }}>
              {formatMoney(meeting.estimate.totalCost)}
            </Typography>
          </Stack>
          {leadCustomer && leadCustomer.quotes.length > 0 && (
            <Button variant="text" color="primary" onClick={onViewQuotes}>
              {t("rail.view_estimate")}
            </Button>
          )}
        </RailCard>
      )}

      <RailCard title={t("rail.timeline")}>
        <Stack spacing={1}>
          {meeting.createdAt && (
            <Typography variant="caption">
              <b>{t("rail.requested")}</b> ·{" "}
              {/^\d+$/.test(String(meeting.createdAt))
                ? moment(Number(meeting.createdAt)).format("MMM D, YYYY")
                : moment(meeting.createdAt).format("MMM D, YYYY")}
            </Typography>
          )}
          <Typography variant="caption">
            <b>{t("rail.status")}</b> ·{" "}
            {meeting.displayStatus === "pending"
              ? t("rail.waiting")
              : meeting.displayStatus === "confirmed"
                ? t("rail.confirmed")
                : meeting.displayStatus === "completed"
                  ? t("rail.completed")
                  : t("rail.cancelled")}
          </Typography>
        </Stack>
      </RailCard>
    </Stack>
  );
};

export default MeetingSummaryRail;
